import { useMemo } from 'react'

import { useMunicipalitiesQuery } from '@/hooks/business/useMunicipalitiesQuery'

interface UseMunicipalitiesListParams {
  provinceId: number
}

interface MunicipalityOption {
  value: string
  label: string
}

interface UseMunicipalitiesListReturn {
  municipalityOptions: MunicipalityOption[]
  isLoadingMunicipalities: boolean
  isErrorMunicipalities: boolean
  errorMunicipalities: Error | null
}

export function useMunicipalitiesList({
  provinceId,
}: UseMunicipalitiesListParams): UseMunicipalitiesListReturn {
  // Validate provinceId to prevent unnecessary API calls
  const isValidProvinceId = Boolean(provinceId && provinceId > 0)

  const {
    municipalities,
    isLoadingMunicipalities,
    isErrorMunicipalities,
    errorMunicipalities,
  } = useMunicipalitiesQuery({
    params: { provinceId },
    options: {
      enabled: isValidProvinceId,
      staleTime: 10 * 60 * 1000, // 10 minutes cache
    },
  })

  // Transform municipalities for select options with proper validation
  const municipalityOptions = useMemo(() => {
    if (!municipalities || !Array.isArray(municipalities)) {
      return []
    }

    return municipalities
      .filter(
        (municipality) => municipality && municipality.id && municipality.name
      )
      .map((municipality) => ({
        value: municipality.id.toString(),
        label: municipality.name,
      }))
  }, [municipalities])

  return {
    municipalityOptions,
    isLoadingMunicipalities: Boolean(
      isLoadingMunicipalities && isValidProvinceId
    ),
    isErrorMunicipalities: Boolean(isErrorMunicipalities && isValidProvinceId),
    errorMunicipalities: isValidProvinceId ? errorMunicipalities : null,
  }
}
